import { useState } from "preact/hooks";
import { JSX } from "preact/jsx-runtime";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import "./Auth.css";

interface AuthProps {
  onAuth: (_login: string, _password: string) => boolean;
}

export function Auth({ onAuth }: AuthProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const redirectPath = sessionStorage.getItem("redirectAfterLogin");
  const hasRedirectMessage = redirectPath && redirectPath !== "/";

  const handleSubmit = (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const isValid = onAuth(username, password);

    if (!isValid) {
      setError(t("auth.error"));
    }

    setIsLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.title")}</h1>
          <LanguageSwitcher />
        </div>

        {hasRedirectMessage && (
          <div className="redirect-message">
            <p>{t("auth.redirect_message", { path: redirectPath })}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">{t("auth.username")}</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e: JSX.TargetedEvent<HTMLInputElement>) =>
                setUsername(e.currentTarget.value)
              }
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t("auth.password")}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e: JSX.TargetedEvent<HTMLInputElement>) =>
                setPassword(e.currentTarget.value)
              }
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="login-btn"
            disabled={isLoading || !username || !password}
          >
            {isLoading ? t("auth.logging_in") : t("auth.login")}
          </button>
        </form>
      </div>
    </div>
  );
}
